import { useCallback, useEffect, useRef, useState } from 'react';

import { messageFrom } from '@bp-files/client';
import { getDownload } from '@/lib/api';
import { VIEWER_COPY } from './viewer.copy';

/**
 * Fetch a file's bytes once, and hand back both the bytes and a URL that can
 * paint them.
 *
 * **The SAS never reaches the DOM.** `components/README.md`'s standing rule is
 * that download is a request and never an `<a href>` (SEC-3) — a credential in
 * an attribute is a credential in the page, in its history, and in anything
 * that scrapes either. `<img src={sasUrl}>` would break that exactly as an
 * anchor does, and would additionally be arguing with the SAS's own
 * `Content-Disposition: attachment`. So the bytes are fetched here and the
 * `<img>` is given an object URL over memory this tab already holds, which is
 * the same exception `ExportButton` relies on.
 *
 * **Opening a file is a download, in full.** There is no second endpoint:
 * `GET /files/{id}/download` validates the grant, checks the plan cap, writes
 * the `file.downloaded` audit row, and meters the transfer, in that fixed order
 * (UPL-8), before a URL exists. Ten photographs is ten audit rows under the
 * reader's name. FIL-8a says so deliberately and ADR-0053 records why.
 *
 * **The cache holds the `Blob`, and not only the object URL.** It used to hold
 * the URL alone, because an `<img src>` was the only consumer and the `Blob`
 * could be dropped the instant `createObjectURL` had seen it. FIL-8b's reader
 * needs the bytes themselves — pdf.js parses an `ArrayBuffer` — so throwing the
 * `Blob` away would mean fetching the object URL back to recover what this hook
 * had a moment earlier, and a second copy of a hundred-megabyte document in a
 * tab that is already holding one. Both are minted together, so a render can
 * still read either without an effect running.
 *
 * **That the cache is state rather than a ref is what makes it seamless.** A
 * hit is *derived during render* — no effect runs and no `setState` fires, so
 * stepping back to a file already loaded shows it in the same frame. Held in a
 * ref it could only be read from an effect, which means a render showing
 * "Loading 0%" before a picture the tab already had, and the back-and-forth
 * comparison this cache exists to serve is exactly where that flicker would
 * land.
 *
 * Nothing here calls `setState` synchronously inside an effect body — the rule
 * `useFiles` states in its own `retry`, and the linter enforces.
 */

/**
 * How many decoded files to keep. Roughly "the current one, and its
 * neighbours".
 *
 * Unchanged from the image-only version, and the argument for it is now
 * stronger rather than weaker: holding the `Blob` as well as the URL means the
 * ceiling bounds real retained memory rather than a handle to it.
 */
const CACHE_LIMIT = 5;

/** The bytes, and the URL minted over them. Always created and released together. */
interface CachedFile {
  blob: Blob;
  href: string;
}

export type FileBlobState =
  | { kind: 'loading'; receivedBytes: number; totalBytes: number }
  | { kind: 'ready'; blob: Blob; src: string }
  | { kind: 'error'; message: string; stage: 'mint' | 'fetch' };

/** What an in-flight or failed attempt has reported, and which attempt it was. */
interface Attempt {
  fileId: string;
  attempt: number;
  value:
    | { kind: 'loading'; receivedBytes: number }
    | { kind: 'error'; message: string; stage: 'mint' | 'fetch' };
}

export interface FileBlob {
  state: FileBlobState;
  /** Re-runs from the mint. Never reuses a URL from a failed attempt. */
  retry: () => void;
}

export function useFileBlob(fileId: string, sizeBytes: number): FileBlob {
  /**
   * Insertion-ordered, so the oldest key is the first one out.
   *
   * **Bounded on purpose.** A 500-photograph folder at 40 MB each would
   * otherwise pin twenty gigabytes of blobs in the tab for as long as the
   * viewer stayed open. Replaced rather than mutated, so a render may depend
   * on it.
   */
  const [cache, setCache] = useState<ReadonlyMap<string, CachedFile>>(() => new Map());
  const [attempted, setAttempted] = useState<Attempt | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);

  const revoked = useRef(new Set<string>());

  /**
   * One place, so no href is ever revoked twice — a double revoke on a live
   * `<img src>` empties the frame with nothing logged.
   */
  const release = useCallback((href: string) => {
    if (revoked.current.has(href)) return;
    revoked.current.add(href);
    URL.revokeObjectURL(href);
  }, []);

  /**
   * The unmount cleanup needs the *latest* cache, not the one its own effect
   * closed over — and an effect that depended on `cache` would run its cleanup,
   * and revoke everything, every time a file was added to it.
   */
  const latestCache = useRef(cache);
  useEffect(() => {
    latestCache.current = cache;
  }, [cache]);

  /** Everything this hook minted goes back when the viewer closes. */
  useEffect(
    () => () => {
      for (const entry of latestCache.current.values()) release(entry.href);
    },
    [release],
  );

  useEffect(() => {
    // A hit costs no audit row and no network at all. Read here only to decide
    // whether to start; the state it implies is derived below, not set.
    if (cache.has(fileId)) return;

    const controller = new AbortController();
    let live = true;

    async function load(id: string): Promise<void> {
      let url: string;

      try {
        ({ url } = await getDownload(id, controller.signal));
      } catch (caught) {
        // The API's own message wherever there is one: a 429 names which cap
        // was reached and who can act on it (PLN-11), which for a reader at a
        // plan-less agency is
        // the sending agency — something no screen here could work out.
        if (!live || controller.signal.aborted) return;
        setAttempted({
          fileId: id,
          attempt,
          value: {
            kind: 'error',
            message: messageFrom(caught, VIEWER_COPY.errors.mint),
            stage: 'mint',
          },
        });
        return;
      }

      try {
        /**
         * `credentials: 'omit'` is load-bearing, not decoration. The SAS *is*
         * the credential; `ensureBlobCors` sets no `allowCredentials` on the
         * storage account, so a credentialed request fails preflight. Going
         * through `request()`/`createTransport` would be worse still — it would
         * attach this app's `Authorization` header to a storage-account origin.
         */
        const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
        if (!response.ok) throw new Error(`blob responded ${String(response.status)}`);

        const blob = await readWithProgress(response, sizeBytes, (receivedBytes) => {
          if (live)
            setAttempted({ fileId: id, attempt, value: { kind: 'loading', receivedBytes } });
        });

        if (!live) return;

        const next = new Map(cache);
        next.set(id, { blob, href: URL.createObjectURL(blob) });

        // Evict from the front — insertion order, so oldest first. Collected
        // and revoked once the map has settled, never inside a state updater.
        const evicted: string[] = [];
        while (next.size > CACHE_LIMIT) {
          const oldest = next.keys().next().value;
          if (oldest === undefined) break;
          const entry = next.get(oldest);
          next.delete(oldest);
          if (entry !== undefined) evicted.push(entry.href);
        }

        setCache(next);
        for (const href of evicted) release(href);
      } catch (caught) {
        // An abort is the caller changing their mind, not a failure to report.
        if (!live || controller.signal.aborted) return;
        if (caught instanceof DOMException && caught.name === 'AbortError') return;

        setAttempted({
          fileId: id,
          attempt,
          value: { kind: 'error', message: VIEWER_COPY.errors.fetch, stage: 'fetch' },
        });
      }
    }

    void load(fileId);

    return () => {
      live = false;
      controller.abort();
    };
  }, [fileId, sizeBytes, attempt, cache, release]);

  const retry = useCallback(() => {
    setAttempt((current) => current + 1);
  }, []);

  /**
   * Derived, in this order, so that changing file needs no `setState` at all:
   * a file already fetched, then whatever this attempt has reported, then
   * loading. An `attempted` naming a different file — or a spent attempt — is
   * last time's answer, and is ignored rather than cleared.
   */
  const cached = cache.get(fileId);
  const reported =
    attempted?.fileId === fileId && attempted.attempt === attempt ? attempted.value : undefined;

  let state: FileBlobState;
  if (cached !== undefined) {
    state = { kind: 'ready', blob: cached.blob, src: cached.href };
  } else if (reported?.kind === 'error') {
    state = reported;
  } else {
    state = {
      kind: 'loading',
      receivedBytes: reported?.receivedBytes ?? 0,
      totalBytes: sizeBytes,
    };
  }

  return { state, retry };
}

/**
 * Read the body, reporting how much has landed.
 *
 * **The denominator comes from the row, not from the response.** The storage
 * account's CORS rule exposes no headers, so `Content-Length` is unreadable
 * from script — `FileSummary.sizeBytes` is the only number available, and it is
 * the one the fixtures are built to tell the truth about.
 *
 * `onProgress` fires only when the whole percent changes. Reporting every chunk
 * of a 40 MB file would spend more time rendering the progress than moving the
 * bytes.
 */
async function readWithProgress(
  response: Response,
  totalBytes: number,
  onProgress: (receivedBytes: number) => void,
): Promise<Blob> {
  const body = response.body;
  // No stream to read (an empty body, or an environment without one) — take
  // the whole thing and report it as arrived.
  if (body === null) {
    const blob = await response.blob();
    onProgress(blob.size);
    return blob;
  }

  const reader = body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  let reportedPercent = -1;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;

    chunks.push(value);
    received += value.length;

    const percent = totalBytes > 0 ? Math.floor((received / totalBytes) * 100) : 0;
    if (percent !== reportedPercent) {
      reportedPercent = percent;
      onProgress(received);
    }
  }

  return new Blob(chunks as BlobPart[], { type: response.headers.get('content-type') ?? '' });
}
